/* ============================================================
   GH-1133 S5 — TrialWallOverlay (design §2.5, §2.8 C, §3.9 item 4).
   Today's card wall, moved to the moment of intent: it renders the SAME
   checkout RequireActiveBilling would show (App.jsx gate branch) over the
   current screen —
     PlanSelectScreen (hosted Stripe checkout with the 7-day trial), or
     TrialCardCaptureContainer when the hosted-trial kill switch applies
     (dathent.hostedCheckout.trial='0' and no Managed Payments).
   Exits: "← Back to my drafts", Esc and browser back close it. The overlay
   pushes ONE history entry with the same URL, so a reload or a checkout
   return never reopens it. Continue writes the resume intent {source, postId,
   plan} and goes to Stripe with the overlay-supplied returnTo (/app for the
   /app/plans route).
   ============================================================ */
import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PlanSelectScreen from '../../screens/PlanSelectScreen.jsx';
import TrialCardCaptureContainer from '../../screens/TrialCardCaptureContainer.jsx';
import { usePlanCatalog } from '../../hooks/usePlanCatalog.js';
import { hostedTrialCheckoutEnabled } from '../../lib/flags.js';
import { resolveManagedPaymentsNewSubscriptions } from '../../lib/managedPayments.js';
import { wallReturnTo, writeResumeIntent } from '../../lib/freePreview.js';
import { trackPreview } from '../../lib/freePreviewEvents.js';

function sessionStore() {
  try { return window.sessionStorage; } catch { return null; }
}

export default function TrialWallOverlay({ wall, entitlements, onClose, onPaid }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { managedPaymentsNewSubscriptions: catalogManagedPayments } = usePlanCatalog();
  const fromPlansRoute = Boolean(wall?.meta?.fromPlansRoute);
  const returnTo = wallReturnTo({ pathname: location.pathname, search: location.search, fromPlansRoute });
  const pushedRef = useRef(false);
  const closingHowRef = useRef(null);
  const closedRef = useRef(false);

  const finish = useCallback((how) => {
    if (closedRef.current) return;
    closedRef.current = true;
    onClose?.(how);
    // The /app/plans route renders the board under the wall; leaving the
    // wall leaves the pricing URL too.
    if (fromPlansRoute) navigate('/app', { replace: true });
  }, [onClose, fromPlansRoute, navigate]);

  // Close via the button / Esc: pop our own history entry when it is still the
  // current one (popstate then finishes), otherwise finish directly.
  const requestClose = useCallback((how) => {
    if (closedRef.current) return;
    let ownEntry = false;
    try { ownEntry = pushedRef.current && window.history.state?.fpWall === true; } catch { ownEntry = false; }
    if (ownEntry) {
      closingHowRef.current = how;
      try { window.history.back(); return; } catch { /* fall through */ }
    }
    finish(how);
  }, [finish]);

  useEffect(() => {
    try {
      window.history.pushState({ ...(window.history.state || {}), fpWall: true }, '', window.location.href);
      pushedRef.current = true;
    } catch { pushedRef.current = false; }
    const onPop = () => { finish(closingHowRef.current || 'browser_back'); };
    const onKey = (event) => { if (event.key === 'Escape') requestClose('esc'); };
    window.addEventListener('popstate', onPop);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // PlanSelectScreen calls this for every plan, the €3.99 Scheduler
  // ('posting', billed today, no trial) included, so the plan goes into the
  // intent: PreviewResumeHost words the after-payment toast from it.
  const beforeCheckout = (planKey) => {
    writeResumeIntent(sessionStore(), { source: wall.source, postId: wall.meta?.postId || null, plan: planKey || null });
    trackPreview('preview_checkout_started', { source: wall.source, plan: planKey });
  };

  const managedPaymentsNewSubscriptions = resolveManagedPaymentsNewSubscriptions({
    catalogFlag: catalogManagedPayments,
    entitlements,
  });
  const hosted = hostedTrialCheckoutEnabled({ managedPaymentsNewSubscriptions });
  const previewContext = {
    source: wall.source,
    thumbUrl: wall.meta?.thumbUrl || null,
    thumbTitle: wall.meta?.thumbTitle || null,
    featureLabel: wall.meta?.featureLabelKey ? t(wall.meta.featureLabelKey) : '',
  };

  return (
    <div className="fpw-overlay" role="dialog" aria-modal="true" aria-label={t('freePreview.nextTitle')}>
      {hosted ? (
        <PlanSelectScreen
          onComplete={onPaid}
          previewContext={previewContext}
          onBack={() => requestClose('back')}
          returnTo={returnTo}
          onBeforeCheckout={beforeCheckout}
        />
      ) : (
        <div className="fpw-killswitch">
          {/* Kill-switch path: TrialCardCaptureContainer also offers the
              Scheduler (no trial), and its selection is internal, so no
              static trial line is rendered here (§2.1 principle 5). */}
          <button type="button" className="fpw-back fpw-back--inline" onClick={() => requestClose('back')}>
            <span aria-hidden="true">←</span> {t('freePreview.wallBack')}
          </button>
          <TrialCardCaptureContainer
            onComplete={(activation) => {
              beforeCheckout(activation?.planKey || null);
              return onPaid?.(activation);
            }}
          />
        </div>
      )}
    </div>
  );
}
